import type { LabeledField } from "@tellescope/types-models";

/**
 * Campaign-attribution params carried on the form's own URL. A key qualifies if it starts with
 * `utm_` (case-insensitively) or is exactly `ours_user_id`, matching legacy's `get_utm_params` /
 * `append_current_utm_params` so a link built for the legacy portal attributes identically here.
 */
const isUtmKey = (key: string): boolean => key.toLowerCase().startsWith("utm_") || key === "ours_user_id";

const currentUtmEntries = (): [string, string][] => {
  if (typeof window === "undefined") return [];
  try {
    return Array.from(new URL(window.location.href).searchParams).filter(([key]) => isUtmKey(key));
  } catch {
    // No parseable page URL (SSR, a test harness, an about:blank frame) — attribution is simply
    // unavailable, which must not stop a submit or a redirect.
    return [];
  }
};

/**
 * Reads the campaign-attribution params off the current URL for the `utm` field of
 * `submit_form_response`, which records them on the response.
 *
 * Returns undefined rather than an empty array when there are none, so the key is omitted from the
 * payload instead of overwriting a stored value with nothing.
 */
export const readUtmParams = (): LabeledField[] | undefined => {
  const entries = currentUtmEntries();
  if (entries.length === 0) return undefined;
  return entries.map(([field, value]) => ({ field, value }));
};

/**
 * Carries the current URL's campaign-attribution params onto a redirect target, so attribution
 * survives a hop between forms or out to a configured destination.
 *
 * A param already present on the target wins — the target was configured deliberately, and legacy's
 * string concatenation would have produced a duplicate key here rather than an override.
 */
export const appendCurrentUtmParams = (targetURL: string): string => {
  const entries = currentUtmEntries();
  if (entries.length === 0) return targetURL;

  try {
    const target = new URL(targetURL);
    entries.forEach(([key, value]) => {
      if (!target.searchParams.has(key)) target.searchParams.append(key, value);
    });
    return target.toString();
  } catch {
    // Attribution is never worth losing the destination — the response is already saved.
    return targetURL;
  }
};
